import React, { useState, useEffect } from "react";
import { FaWhatsapp } from "react-icons/fa";
import Navbar from "../components/Navbar";
import Sidebar from "../components/Sidebar";
import { useAuth } from "../context/AuthContext";
import { fetchUpdates, fetchMyUpdates, toggleUpdateReaction } from "../api/api";

import harvestIcon from "../assets/icons/harvest.svg";
import eventIcon from "../assets/icons/event.svg";
import alertIcon from "../assets/icons/alert.svg";
import newsIcon from "../assets/icons/news.svg";
import defaultIcon from "../assets/icons/default.svg";

// Same set/order as the mobile app's UpdatesScreen (REACTION_SET) — Thumbsup,
// Heart, Sprout, Party Popper — kept as plain emoji to match mobile exactly
// rather than swapping in a web icon library.
const REACTIONS = ["👍", "❤️", "🌱", "🎉"];

// Same idea as the mobile app's CONTACT_EMOJI/WHATSAPP_EMOJI — distinct
// reactions (not general-purpose ones) that signal "please reach out to me
// about this update" over a specific channel, sent the same way
// (toggleUpdateReaction) as every other reaction, just styled apart from
// them. 📲 stands in for WhatsApp (no official logo character in Unicode).
const CONTACT_EMOJI = "📞";
const WHATSAPP_EMOJI = "📲";

// Local fallback icons, one per Update.category (see app/models/update.py) —
// some seeded updates carry an unreachable image_url (e.g. an
// "https://example.com/..." placeholder no one ever uploaded a real photo
// for), so the banner below falls back to these bundled icons on load error
// instead of showing a broken-image glyph.
const CATEGORY_ICONS = {
  harvest: harvestIcon,
  event: eventIcon,
  alert: alertIcon,
  news: newsIcon,
};

function categoryIcon(category) {
  return CATEGORY_ICONS[category] || defaultIcon;
}

// Same category set, labels, and banner colors as the mobile app's
// UpdatesScreen (CATEGORIES/TAG_COLORS) — category values must match the
// backend's UpdateCategory enum (harvest/event/infra/alert/news). 'infra'
// is no longer offered as a filter chip (see mobile's UpdatesScreen for
// the same change) — any existing infra-tagged update still shows under
// "All", falling back to the default category icon/color.
const CATEGORIES = [
  { label: "All", value: "all" },
  { label: "Harvest", value: "harvest" },
  { label: "Events", value: "event" },
  { label: "Alerts", value: "alert" },
  { label: "News", value: "news" },
];

const CATEGORY_LABELS = {
  harvest: "Harvest",
  event: "Events",
  alert: "Alerts",
  news: "News",
};

const TAG_COLORS = {
  harvest: "#1B4D2E",
  event: "#5D4037",
  alert: "#C0392B",
  news: "#1B4D2E",
};

function categoryLabel(category) {
  return CATEGORY_LABELS[category] || (category ? category.charAt(0).toUpperCase() + category.slice(1) : "Update");
}

function errorMessage(err) {
  const detail = err.response?.data?.detail;
  return detail || "Something went wrong. Please try again.";
}

function reactionCounts(reactions) {
  const counts = {};
  for (const r of reactions || []) {
    counts[r.emoji] = (counts[r.emoji] || 0) + 1;
  }
  return counts;
}

function myReactedEmojis(reactions, userId) {
  const mine = new Set();
  for (const r of reactions || []) {
    if (r.user_id === userId) mine.add(r.emoji);
  }
  return mine;
}

function CustomerUpdatesFeed() {

  const { user } = useAuth();

  const [updates, setUpdates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [reactingId, setReactingId] = useState(null);
  const [failedImageIds, setFailedImageIds] = useState(new Set());
  // One exclusive selection across the whole filter row (All/Harvest/.../News
  // /My Updates) — same single-value-tab pattern as the mobile app's
  // activeTab, so picking "My Updates" always clears any category and vice
  // versa instead of the two being independently toggleable.
  const [activeTab, setActiveTab] = useState("all");
  const [searchText, setSearchText] = useState("");
  const myUpdatesOnly = activeTab === "myUpdates";

  useEffect(() => {
    setLoading(true);
    setError("");
    const request = myUpdatesOnly ? fetchMyUpdates() : fetchUpdates();
    request
      .then(setUpdates)
      .catch((err) => setError(errorMessage(err)))
      .finally(() => setLoading(false));
  }, [myUpdatesOnly]);

  const handleReact = async (updateId, emoji) => {
    if (!user) {
      window.alert("Log in to react to updates.");
      return;
    }
    setReactingId(updateId);
    try {
      await toggleUpdateReaction(updateId, emoji);
      const refreshed = await fetchUpdates();
      setUpdates(refreshed);
    } catch (err) {
      window.alert(errorMessage(err));
    } finally {
      setReactingId(null);
    }
  };

  return (
    <>
      <Navbar />
      <Sidebar />

      <div className="main-content">

        <div className="updates-feed-page">

          <h2>Community Updates</h2>

          <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", alignItems: "center", marginBottom: "12px" }}>
            <input
              type="search"
              placeholder="Search updates…"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ flex: "1 1 220px", padding: "8px 12px", borderRadius: "8px", border: "1px solid #ddd" }}
            />
          </div>

          <div className="update-category-filter">
            {CATEGORIES.map((cat) => (
              <button
                key={cat.value}
                className={activeTab === cat.value ? "active" : ""}
                onClick={() => setActiveTab(cat.value)}
              >
                {cat.label}
              </button>
            ))}
            {user && (
              <button
                type="button"
                className={activeTab === "myUpdates" ? "active" : ""}
                onClick={() => setActiveTab("myUpdates")}
              >
                My Updates
              </button>
            )}
          </div>

          {loading && <p>Loading updates…</p>}
          {error && <p className="login-error" style={{ color: "red" }}>{error}</p>}

          {(() => {
            const q = searchText.trim().toLowerCase();
            // While searching, look across every category tab instead of
            // just whichever one happens to be active — a search is about
            // finding a specific update, not one scoped to the current tab.
            const filteredUpdates = updates
              .filter((item) => q || activeTab === "all" || activeTab === "myUpdates" || item.category_name === activeTab)
              .filter((item) =>
                !q ||
                (item.title || "").toLowerCase().includes(q) ||
                (item.body || "").toLowerCase().includes(q)
              );

            if (!loading && !error && filteredUpdates.length === 0) {
              return <p>{q ? "No updates match your search." : "No updates in this category yet."}</p>;
            }

            return filteredUpdates.map((item) => {
            const counts = reactionCounts(item.reactions);
            const mine = myReactedEmojis(item.reactions, user?.id);
            const hasRealPhoto = /^https?:\/\//.test(item.image_url || "") && !failedImageIds.has(item.id);

            return (
              <div key={item.id} className="update-card">

                <div
                  className={hasRealPhoto ? "update-banner update-banner--photo" : "update-banner"}
                  style={{ backgroundColor: TAG_COLORS[item.category_name] || "#2e7d32" }}
                >
                  {hasRealPhoto ? (
                    <img
                      src={item.image_url}
                      alt={item.title}
                      className="update-banner-photo"
                      onError={() => setFailedImageIds((prev) => new Set(prev).add(item.id))}
                    />
                  ) : (
                    <img src={categoryIcon(item.category_name)} alt={item.category_name} className="update-banner-icon" />
                  )}
                  <span className="update-banner-tag">{categoryLabel(item.category_name)}</span>
                  <span className="update-banner-date">
                    {new Date(item.created_at).toLocaleDateString()}
                  </span>
                </div>

                <div className="update-card-body">

                <h3>{item.title}</h3>

                <p className="update-date">
                  {item.location || ""}
                </p>

                <p className="update-description">{item.body}</p>

                <div className="reaction-buttons">
                  {REACTIONS.map((emoji) => (
                    <button
                      key={emoji}
                      className={mine.has(emoji) ? "reacted" : ""}
                      onClick={() => handleReact(item.id, emoji)}
                      disabled={reactingId === item.id}
                    >
                      {emoji} {counts[emoji] || 0}
                    </button>
                  ))}

                  {/* "Suggest: Contact me" / "...on WhatsApp" — same idea as
                      the mobile app's CONTACT_EMOJI/WHATSAPP_EMOJI chips:
                      distinct reactions, sent the same way as the ones
                      above (toggleUpdateReaction), just styled apart from
                      them via contact-btn/whatsapp-btn. The WhatsApp button
                      still toggles the plain WHATSAPP_EMOJI string like any
                      other reaction, but renders the real WhatsApp logo
                      instead of a Unicode stand-in. */}
                  <button
                    className={`contact-btn${mine.has(CONTACT_EMOJI) ? " reacted" : ""}`}
                    onClick={() => handleReact(item.id, CONTACT_EMOJI)}
                    disabled={reactingId === item.id}
                  >
                    {CONTACT_EMOJI} {counts[CONTACT_EMOJI] || 0}
                  </button>

                  <button
                    className={`whatsapp-btn${mine.has(WHATSAPP_EMOJI) ? " reacted" : ""}`}
                    onClick={() => handleReact(item.id, WHATSAPP_EMOJI)}
                    disabled={reactingId === item.id}
                  >
                    <FaWhatsapp size={16} /> {counts[WHATSAPP_EMOJI] || 0}
                  </button>
                </div>

                </div>

              </div>
            );
            });
          })()}

        </div>

      </div>
    </>
  );
}

export default CustomerUpdatesFeed;
